import * as signalR from '@microsoft/signalr';
import { LocalStorageKeys } from '@/project/localStorageKeys';
import { HttpTransportType } from '@microsoft/signalr';
import { computed, ComputedRef, ref } from 'vue';
import { LocalStorageService } from '@/core/storage/storage.service';
import bus from '@/core/bus';
import { createURLWithParams } from '../constants';

const TIMEOUT_BETWEEN_RECONNECT = 5000;
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const defaultReconnectPolicy: signalR.IRetryPolicy = {
    nextRetryDelayInMilliseconds: retryContext => {
        return TIMEOUT_BETWEEN_RECONNECT;
    },
};

export abstract class SignalRClient {
    protected connection: signalR.HubConnection;
    private _isConnected = ref(false);
    private _connectionId = ref<string | undefined>('');

    constructor(url: string, logLevel: signalR.LogLevel, reconnectPolicy: signalR.IRetryPolicy = defaultReconnectPolicy, autoStart = true) {
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(createURLWithParams(url, { 
                timezone: TIME_ZONE,
            }), {
                accessTokenFactory: () => LocalStorageService.getItem(LocalStorageKeys.TOKEN) ?? '',
                skipNegotiation: false,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect(reconnectPolicy)
            .configureLogging(logLevel)
            .build();

        if (autoStart) {
            this.start();
        }

        this.connection.onreconnected((connectionId) => {
            this._isConnected.value = true;
            this._connectionId.value = connectionId;
            bus.emit('SIGNALR_RECONNECT', connectionId);
        });

        this.connection.onreconnecting(() => {
            this._isConnected.value = false;
        });
    }

    public async reconnect(): Promise<void> {
        await this.stop();
        await this.start();
    }

    public get connectionId(): ComputedRef<string | undefined> {
        return computed(() => this._connectionId.value);
    }

    public get isConnected(): ComputedRef<boolean> {
        return computed(() => this._isConnected.value);
    }

    public async start(): Promise<void> {
        if (this.connection.state === signalR.HubConnectionState.Connected || this.connection.state === signalR.HubConnectionState.Connecting) {
            return;
        }

        try {
            await this.connection.start();
            this._isConnected.value = true;
            this._connectionId.value = this.connection?.connectionId ?? undefined;
            bus.emit('SIGNALR_RECONNECT', this._connectionId.value);
        } catch (e) {
            console.error(e);
        }
    }

    public async stop(): Promise<void> {
        if (this.connection.state === signalR.HubConnectionState.Disconnected) {
            this._isConnected.value = false;
            this._connectionId.value = undefined;
            bus.emit('SIGNALR_DISCONNECT');
            return;
        }

        try {
            await this.connection.stop();
            this._isConnected.value = false;
            this._connectionId.value = undefined;
            bus.emit('SIGNALR_DISCONNECT');
        } catch (e) {
            console.error(e);
        }
    }

    public addEvent(event: string, callback: (...args: any[]) => void): void {
        this.connection.on(event, callback);
    }

    public removeEvent(event: string, callback: (...args: any[]) => void): void {
        this.connection.off(event, callback);
    }

    /*
         Returns a promise that is resolved when the client has sent the invocation to the server,
         or an error occurred. The server may still be handling the invocation when the promise resolves.
     */
    public async sendMessage(methodName: string, ...args: any[]): Promise<void> {
        await this.connection.send(methodName, args);
    }

    /*
        Returns a promise that is resolved when the server has finished invoking the method (or an error occurred).
        In addition, the Invoke promise can receive a result from the server method, if the server returns a result.
    */
    public async invoke<T>(methodName: string, ...args: any[]): Promise<T> {
        return await this.connection.invoke<T>(methodName, args);
    }
}
